import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { nocService } from '../../services/apiService';

const Approvals = () => {
  const [allNocRequests, setAllNocRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userDepartment, setUserDepartment] = useState(null);
  const [activeTab, setActiveTab] = useState('pending'); // 'pending', 'approved', 'rejected'
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  
  // Bulk selection states
  const [selectedNocs, setSelectedNocs] = useState(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);
  
  // Filter states
  const [selectedDepartment, setSelectedDepartment] = useState('ALL');
  const [selectedSemester, setSelectedSemester] = useState('ALL');
  const [selectedNocType, setSelectedNocType] = useState('ALL');
  const [sortBy, setSortBy] = useState('date-desc'); // date-desc, date-asc, name-asc, name-desc, usn-asc, usn-desc
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  useEffect(() => {
    // Get user role and department from localStorage
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setUserRole(user.role);
    setUserDepartment(user.department);
    
    // Set default department filter for HODs only if they have a valid department
    // NA, null, undefined, or empty string should default to 'ALL'
    if (user.role === 'HOD' && user.department && user.department !== 'NA' && user.department.trim() !== '') {
      setSelectedDepartment(user.department);
    } else {
      setSelectedDepartment('ALL');
    }
  }, []);

  // Refetch data when tab changes
  useEffect(() => {
    if (userRole) {
      fetchAllApprovals();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, userRole]);

  useEffect(() => {
    filterRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allNocRequests, activeTab, userRole, selectedDepartment, selectedSemester, selectedNocType]);
  
  useEffect(() => {
    // Reset to page 1 when filters or sort changes
    setCurrentPage(1);
  }, [activeTab, selectedDepartment, selectedSemester, selectedNocType, sortBy]);

  const fetchAllApprovals = async () => {
    try {
      let data;
      // Use optimized endpoints based on active tab
      if (activeTab === 'pending') {
        data = await nocService.getPendingNOCRequests({ limit: 1000 });
      } else if (activeTab === 'approved') {
        data = await nocService.getApprovedNOCRequests({ limit: 1000 });
      } else if (activeTab === 'rejected') {
        data = await nocService.getRejectedNOCRequests({ limit: 1000 });
      } else {
        // Fallback to all requests
        data = await nocService.getNOCRequests({ limit: 1000 });
      }
      setAllNocRequests(data.nocRequests);
    } catch (error) {
      toast.error('Failed to fetch approvals');
    } finally {
      setLoading(false);
    }
  };

  const filterRequests = () => {
    if (!userRole) return;

    const filtered = allNocRequests.filter((noc) => {
      
      // For tab-based endpoints, the backend already filtered by status
      // We just need to apply sequential approval logic for pending tab
      if (activeTab === 'pending') {
        // Double-check that user can approve (sequential logic)
        if (!canUserApprove(noc)) return false;
      }
      
      // Department filter
      const isFilteringDepartment = selectedDepartment !== 'ALL' && 
                                    selectedDepartment !== 'NA' && 
                                    selectedDepartment !== null && 
                                    selectedDepartment !== undefined && 
                                    selectedDepartment.trim() !== '';
      
      if (isFilteringDepartment && noc.student?.department !== selectedDepartment) {
        return false;
      }
      
      // Semester filter
      if (selectedSemester !== 'ALL' && noc.student?.semester !== selectedSemester) {
        return false;
      }
      
      // NOC Type filter
      if (selectedNocType !== 'ALL' && noc.nocType !== selectedNocType) {
        return false;
      }
      
      return true;
    });

    setFilteredRequests(filtered);
  };

  const canUserApprove = (noc) => {
    // If the NOC is fully approved, no one can change it
    if (noc.overallStatus === 'APPROVED') return false;

    // IMPORTANT: For pending tab, we should NOT include rejected items
    // Rejected items should only appear in the "Rejected by Me" tab
    // This check should NOT return true for rejected items in pending context
    
    // Sequential approval logic for pending approvals
    const approvalOrder = ['ACCOUNTS_SECTION', 'LIBRARY_STAFF', 'HOD', 'EXAM_SECTION'];
    const userIndex = approvalOrder.indexOf(userRole);
    
    if (userIndex === -1) return false;
    
    // Check if all previous approvers have approved
    for (let i = 0; i < userIndex; i++) {
      const prevApproval = noc.approvals.find(a => a.approverRole === approvalOrder[i]);
      if (prevApproval?.status !== 'APPROVED') {
        return false;
      }
    }
    
    return true;
  };

  const canUserRejectApproved = (noc) => {
    // Only allow rejection of approved NOCs if user is not EXAM_SECTION
    if (userRole === 'EXAM_SECTION') return false;

    // Check if any role after current user has approved
    const approvalOrder = ['ACCOUNTS_SECTION', 'LIBRARY_STAFF', 'HOD', 'EXAM_SECTION'];
    const userIndex = approvalOrder.indexOf(userRole);
    
    if (userIndex === -1) return false;

    // Check if any role after this user has approved
    for (let i = userIndex + 1; i < approvalOrder.length; i++) {
      const nextApproval = noc.approvals.find(a => a.approverRole === approvalOrder[i]);
      if (nextApproval && nextApproval.status === 'APPROVED') {
        return false; // Cannot reject if later roles have approved
      }
    }

    return true;
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      toast.error('Please enter a USN to search');
      return;
    }

    setLoading(true);
    try {
      // Query database directly using dedicated search endpoint
      // This searches across ALL NOC requests in the database, not just pending/rejected
      const data = await nocService.searchNOCByUSN(searchTerm.trim());
      
      const results = data.nocRequests;

      if (results.length === 0) {
        toast.info(`No NOC requests found for USN: ${searchTerm}`);
        setSearchResults([]);
      } else {
        setSearchResults(results);
        toast.success(`Found ${results.length} NOC request(s) for USN: ${searchTerm}`);
      }
    } catch (error) {
      toast.error('Failed to search NOC requests');
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSearchResults(null);
    setSelectedNocs(new Set());
  };

  // Toggle select all
  const handleSelectAll = () => {
    if (selectedNocs.size === displayRequests.length) {
      setSelectedNocs(new Set());
    } else {
      const approvableNocs = displayRequests
        .filter(noc => {
          if (activeTab === 'pending') {
            return canUserApprove(noc);
          }
          return activeTab === 'rejected'; // Can bulk approve rejected items
        })
        .map(noc => noc.id);
      setSelectedNocs(new Set(approvableNocs));
    }
  };

  // Toggle individual selection
  const handleSelectNoc = (nocId) => {
    const newSelected = new Set(selectedNocs);
    if (newSelected.has(nocId)) {
      newSelected.delete(nocId);
    } else {
      newSelected.add(nocId);
    }
    setSelectedNocs(newSelected);
  };

  // Bulk approve
  const handleBulkApprove = async () => {
    if (selectedNocs.size === 0) {
      toast.error('Please select at least one NOC request');
      return;
    }

    if (!window.confirm(`Are you sure you want to approve ${selectedNocs.size} NOC request(s)?`)) {
      return;
    }

    setBulkProcessing(true);
    try {
      await nocService.bulkProcessNOC(Array.from(selectedNocs), 'APPROVED', '');
      toast.success(`Successfully approved ${selectedNocs.size} NOC request(s)!`);
      setSelectedNocs(new Set());
      await fetchAllApprovals();
      if (searchResults) {
        handleSearch();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to process bulk approval');
    } finally {
      setBulkProcessing(false);
    }
  };

  // Bulk reject
  const handleBulkReject = async () => {
    if (selectedNocs.size === 0) {
      toast.error('Please select at least one NOC request');
      return;
    }

    const remarks = window.prompt(`Enter rejection reason for ${selectedNocs.size} NOC request(s):`);
    if (!remarks || !remarks.trim()) {
      toast.error('Rejection reason is required');
      return;
    }

    setBulkProcessing(true);
    try {
      await nocService.bulkProcessNOC(Array.from(selectedNocs), 'REJECTED', remarks);
      toast.success(`Successfully rejected ${selectedNocs.size} NOC request(s)!`);
      setSelectedNocs(new Set());
      await fetchAllApprovals();
      if (searchResults) {
        handleSearch();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to process bulk rejection');
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleApproval = async (nocId, status, remarks = '') => {
    setProcessingId(nocId);
    try {
      await nocService.processNOCApproval(nocId, status, remarks);
      toast.success(`NOC request ${status.toLowerCase()} successfully!`);
      await fetchAllApprovals();
      if (searchResults) {
        // Re-run search after action
        handleSearch();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to process approval');
    } finally {
      setProcessingId(null);
    }
  };

  const nocTypeLabels = {
    TRANSFER_CERTIFICATE: 'Transfer Certificate',
    COURSE_COMPLETION: 'Course Completion',
    INTERNSHIP: 'Internship',
    SCHOLARSHIP: 'Scholarship',
    EXAM_HALL_TICKET: 'Exam Hall Ticket',
    LIBRARY_CLEARANCE: 'Library Clearance',
    FEE_CLEARANCE: 'Fee Clearance',
    OTHER: 'Other'
  };

  // Sorting logic
  const sortRequests = (requests) => {
    const sorted = [...requests];
    
    switch (sortBy) {
      case 'date-desc':
        return sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      case 'date-asc':
        return sorted.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      case 'name-asc':
        return sorted.sort((a, b) => a.student.name.localeCompare(b.student.name));
      case 'name-desc':
        return sorted.sort((a, b) => b.student.name.localeCompare(a.student.name));
      case 'usn-asc':
        return sorted.sort((a, b) => a.student.usn.localeCompare(b.student.usn));
      case 'usn-desc':
        return sorted.sort((a, b) => b.student.usn.localeCompare(a.student.usn));
      default:
        return sorted;
    }
  };

  // Pagination logic for display
  const baseRequests = searchResults !== null ? searchResults : filteredRequests;
  const sortedRequests = sortRequests(baseRequests);
  const allDisplayRequests = sortedRequests;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayRequests = allDisplayRequests.slice(startIndex, endIndex);
  
  // Update total pages when display requests change
  useEffect(() => {
    const pages = Math.ceil(allDisplayRequests.length / itemsPerPage);
    setTotalPages(pages || 1);
    // Reset to page 1 if current page exceeds total pages
    if (currentPage > pages && pages > 0) {
      setCurrentPage(1);
    }
  }, [allDisplayRequests.length, itemsPerPage, currentPage]);

  const getTabCounts = () => {
    const counts = {
      pending: 0,
      approved: 0,
      rejected: 0
    };

    // Count based on what would be shown in each tab (using same filter logic)
    allNocRequests.forEach((noc) => {
      const userApproval = noc.approvals.find(a => a.approverRole === userRole);
      
      // Apply department filter for HODs
      // Treat 'NA', null, undefined, or empty string as 'ALL' (show all departments)
      const isFilteringDepartment = selectedDepartment !== 'ALL' && 
                                    selectedDepartment !== 'NA' && 
                                    selectedDepartment !== null && 
                                    selectedDepartment !== undefined && 
                                    selectedDepartment.trim() !== '';
      
      if (isFilteringDepartment && noc.student?.department !== selectedDepartment) {
        return;
      }
      
      // Apply semester filter
      if (selectedSemester !== 'ALL' && noc.student?.semester !== selectedSemester) {
        return;
      }
      
      // Apply NOC type filter
      if (selectedNocType !== 'ALL' && noc.nocType !== selectedNocType) {
        return;
      }
      
      // Count for each tab
      if (userApproval?.status === 'PENDING' && canUserApprove(noc)) {
        counts.pending++;
      }
      
      if (userApproval?.status === 'APPROVED') {
        counts.approved++;
      }
      
      if (userApproval?.status === 'REJECTED') {
        counts.rejected++;
      }
    });

    return counts;
  };

  const tabCounts = getTabCounts();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-200px)] bg-slate-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">NOC Approvals</h1>
            <p className="text-slate-600 mt-2">Review, approve, or modify NOC requests</p>
          </div>
          <button
            onClick={async () => {
              setLoading(true);
              await fetchAllApprovals();
              if (searchResults) {
                await handleSearch();
              }
              toast.success('Data refreshed successfully!');
            }}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Refresh data"
          >
            <svg 
              className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
              />
            </svg>
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            Search by USN (searches entire database)
          </label>
          <div className="flex gap-3">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Enter student USN (e.g., 1AJ23CS001) - searches all NOC requests"
              className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            <button
              onClick={handleSearch}
              disabled={loading}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Search
            </button>
            {searchResults !== null && (
              <button
                onClick={clearSearch}
                className="px-6 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
              >
                Clear
              </button>
            )}
          </div>
          {searchResults !== null && (
            <div className="mt-3 text-sm text-slate-600">
              {searchResults.length > 0 ? (
                <span className="text-green-600 font-medium">
                  ✓ Found {searchResults.length} result(s) for "{searchTerm}"
                </span>
              ) : (
                <span className="text-orange-600 font-medium">
                  ⚠️ No NOC requests found for USN "{searchTerm}"
                </span>
              )}
            </div>
          )}
        </div>

        {/* Filters */}
        {searchResults === null && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h3 className="text-sm font-medium text-slate-700 mb-4 flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filter & Sort NOC Requests
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Department Filter */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Department
                </label>
                <select
                  value={selectedDepartment}
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="ALL">All Departments</option>
                  <option value="CS">CS - Computer Science</option>
                  <option value="CD">CD - Data Science</option>
                  <option value="CI">CI - Artificial Intelligence</option>
                  <option value="CY">CY - Cyber Security</option>
                  <option value="EC">EC - Electronics & Communication</option>
                  <option value="ME">ME - Mechanical</option>
                </select>
                {userRole === 'HOD' && userDepartment && (
                  <p className="text-xs text-slate-500 mt-1">
                    Default: {userDepartment} (Your Department)
                  </p>
                )}
              </div>

              {/* Semester Filter */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Semester
                </label>
                <select
                  value={selectedSemester}
                  onChange={(e) => setSelectedSemester(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="ALL">All Semesters</option>
                  <option value="SEM_1">Semester 1</option>
                  <option value="SEM_2">Semester 2</option>
                  <option value="SEM_3">Semester 3</option>
                  <option value="SEM_4">Semester 4</option>
                  <option value="SEM_5">Semester 5</option>
                  <option value="SEM_6">Semester 6</option>
                  <option value="SEM_7">Semester 7</option>
                  <option value="SEM_8">Semester 8</option>
                </select>
              </div>

              {/* NOC Type Filter */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  NOC Type
                </label>
                <select
                  value={selectedNocType}
                  onChange={(e) => setSelectedNocType(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="ALL">All Types</option>
                  <option value="TRANSFER_CERTIFICATE">Transfer Certificate</option>
                  <option value="COURSE_COMPLETION">Course Completion</option>
                  <option value="INTERNSHIP">Internship</option>
                  <option value="SCHOLARSHIP">Scholarship</option>
                  <option value="EXAM_HALL_TICKET">Exam Hall Ticket</option>
                  <option value="LIBRARY_CLEARANCE">Library Clearance</option>
                  <option value="FEE_CLEARANCE">Fee Clearance</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>

              {/* Sort By */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                  </svg>
                  Sort By
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="date-desc">Newest First</option>
                  <option value="date-asc">Oldest First</option>
                  <option value="name-asc">Name (A-Z)</option>
                  <option value="name-desc">Name (Z-A)</option>
                  <option value="usn-asc">USN (Ascending)</option>
                  <option value="usn-desc">USN (Descending)</option>
                </select>
              </div>
            </div>

            {/* Active Filters Display */}
            {((selectedDepartment !== 'ALL' && selectedDepartment !== 'NA' && selectedDepartment) || selectedSemester !== 'ALL' || selectedNocType !== 'ALL') && (
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="text-sm text-slate-600">Active Filters:</span>
                {(selectedDepartment !== 'ALL' && selectedDepartment !== 'NA' && selectedDepartment) && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm">
                    Department: {selectedDepartment}
                    <button
                      onClick={() => setSelectedDepartment('ALL')}
                      className="hover:text-indigo-900"
                    >
                      ×
                    </button>
                  </span>
                )}
                {selectedSemester !== 'ALL' && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm">
                    Semester: {selectedSemester.replace('SEM_', '')}
                    <button
                      onClick={() => setSelectedSemester('ALL')}
                      className="hover:text-indigo-900"
                    >
                      ×
                    </button>
                  </span>
                )}
                {selectedNocType !== 'ALL' && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm">
                    Type: {nocTypeLabels[selectedNocType]}
                    <button
                      onClick={() => setSelectedNocType('ALL')}
                      className="hover:text-indigo-900"
                    >
                      ×
                    </button>
                  </span>
                )}
                <button
                  onClick={() => {
                    // Always clear to 'ALL', don't default back to user department
                    setSelectedDepartment('ALL');
                    setSelectedSemester('ALL');
                    setSelectedNocType('ALL');
                  }}
                  className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  Clear All Filters
                </button>
              </div>
            )}
          </div>
        )}

        {/* Tabs */}
        {searchResults === null && (
          <div className="bg-white rounded-lg shadow-md mb-6">
            <div className="flex border-b border-slate-200">
              <button
                onClick={() => {
                  setLoading(true);
                  setActiveTab('pending');
                }}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'pending'
                    ? 'border-b-2 border-indigo-600 text-indigo-600'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                disabled={loading}
              >
                ⏳ Pending Approval
                {tabCounts.pending > 0 && (
                  <span className="ml-2 px-2 py-1 bg-indigo-100 text-indigo-600 rounded-full text-xs">
                    {tabCounts.pending}
                  </span>
                )}
              </button>
              <button
                onClick={() => {
                  setLoading(true);
                  setActiveTab('approved');
                }}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'approved'
                    ? 'border-b-2 border-green-600 text-green-600'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                disabled={loading}
              >
                ✓ Approved by Me
                {tabCounts.approved > 0 && (
                  <span className="ml-2 px-2 py-1 bg-green-100 text-green-600 rounded-full text-xs">
                    {tabCounts.approved}
                  </span>
                )}
              </button>
              <button
                onClick={() => {
                  setLoading(true);
                  setActiveTab('rejected');
                }}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'rejected'
                    ? 'border-b-2 border-red-600 text-red-600'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                disabled={loading}
              >
                ✗ Rejected by Me
                {tabCounts.rejected > 0 && (
                  <span className="ml-2 px-2 py-1 bg-red-100 text-red-600 rounded-full text-xs">
                    {tabCounts.rejected}
                  </span>
                )}
              </button>
            </div>
          </div>
        )}
        
        {/* Results Summary and Bulk Actions */}
        {searchResults === null && !loading && allDisplayRequests.length > 0 && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-6">
            {/* Pagination info at top */}
            {totalPages > 1 && (
              <div className="text-sm text-indigo-600 mb-3 text-right">
                Page {currentPage} of {totalPages}
              </div>
            )}
            
            {/* Bulk Actions Bar */}
            {(activeTab === 'pending' || activeTab === 'rejected') && displayRequests.length > 0 && (
              <div>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 cursor-pointer hover:bg-indigo-100 px-3 py-2 rounded-lg transition-colors">
                      <input
                        type="checkbox"
                        checked={selectedNocs.size > 0 && selectedNocs.size === displayRequests.filter(noc => activeTab === 'pending' ? canUserApprove(noc) : activeTab === 'rejected').length}
                        onChange={handleSelectAll}
                        className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-2 focus:ring-indigo-500"
                      />
                      <span className="text-sm font-medium text-slate-700">
                        Select All {activeTab === 'pending' ? 'Approvable' : ''}
                      </span>
                    </label>
                    {selectedNocs.size > 0 && (
                      <span className="text-sm font-medium text-indigo-700 bg-indigo-100 px-3 py-1 rounded-full">
                        {selectedNocs.size} selected
                      </span>
                    )}
                  </div>
                  
                  {/* Showing count and filter info */}
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-indigo-700 font-medium">
                      📊 Showing {allDisplayRequests.length} request{allDisplayRequests.length !== 1 ? 's' : ''}
                    </span>
                    {(selectedDepartment !== 'ALL' || selectedSemester !== 'ALL' || selectedNocType !== 'ALL') && (
                      <span className="text-indigo-600">
                        (filtered from {filteredRequests.length} total)
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Bulk action buttons */}
                {selectedNocs.size > 0 && (
                  <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-indigo-200">
                    <button
                      onClick={handleBulkApprove}
                      disabled={bulkProcessing}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm"
                    >
                      {bulkProcessing ? (
                        <>
                          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Processing...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Approve Selected ({selectedNocs.size})
                        </>
                      )}
                    </button>
                    {activeTab === 'pending' && (
                      <button
                        onClick={handleBulkReject}
                        disabled={bulkProcessing}
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm"
                      >
                        {bulkProcessing ? (
                          <>
                            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Processing...
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            Reject Selected ({selectedNocs.size})
                          </>
                        )}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* If no bulk actions available, show count with pagination */}
            {!(activeTab === 'pending' || activeTab === 'rejected') && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-indigo-700 font-medium">
                  📊 Showing {allDisplayRequests.length} request{allDisplayRequests.length !== 1 ? 's' : ''}
                </span>
                {(selectedDepartment !== 'ALL' || selectedSemester !== 'ALL' || selectedNocType !== 'ALL') && (
                  <span className="text-indigo-600">
                    (filtered from {filteredRequests.length} total)
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-lg shadow-md p-12">
            <div className="flex flex-col items-center justify-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mb-4"></div>
              <p className="text-slate-600 font-medium">
                Loading {activeTab === 'pending' ? 'pending' : activeTab === 'approved' ? 'approved' : 'rejected'} NOC requests...
              </p>
            </div>
          </div>
        )}

        {/* Empty State and NOC List */}
        {!loading && displayRequests.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="text-6xl mb-4">
              {searchResults !== null ? '🔍' : activeTab === 'pending' ? '✅' : activeTab === 'approved' ? '👍' : '📋'}
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              {searchResults !== null 
                ? 'No Results Found' 
                : activeTab === 'pending' 
                ? 'All Caught Up!' 
                : activeTab === 'approved'
                ? 'No Approved Requests'
                : 'No Rejected Requests'}
            </h3>
            <p className="text-slate-600">
              {searchResults !== null
                ? `No NOC requests found for USN "${searchTerm}"`
                : activeTab === 'pending'
                ? 'There are no NOC requests requiring your attention at this time.'
                : activeTab === 'approved'
                ? 'You have not approved any NOC requests yet.'
                : 'You have not rejected any NOC requests. Rejected requests can be edited and approved later.'}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {displayRequests.map((noc) => {
              const currentUserApproval = noc.approvals.find(a => a.approverRole === userRole);
              const hasRejected = currentUserApproval?.status === 'REJECTED';
              const hasApproved = currentUserApproval?.status === 'APPROVED';
              const canApproveNow = canUserApprove(noc);
              const isSelected = selectedNocs.has(noc.id);
              const isSelectable = (activeTab === 'pending' && canApproveNow) || activeTab === 'rejected';

              return (
              <div key={noc.id} className={`bg-white rounded-xl shadow-lg overflow-hidden transition-all ${
                isSelected ? 'ring-4 ring-indigo-500' : ''
              } ${
                hasRejected ? 'border-2 border-red-300' : 
                hasApproved ? 'border-2 border-green-300' : 
                !canApproveNow && currentUserApproval?.status === 'PENDING' ? 'border-2 border-yellow-300' : ''
              }`}>
                {hasRejected && (
                  <div className="bg-red-50 border-b border-red-200 px-6 py-3">
                    <p className="text-sm text-red-800 font-medium">
                      ⚠️ You previously rejected this request. You can change your decision and approve it below.
                    </p>
                  </div>
                )}
                {hasApproved && (
                  <div className="bg-green-50 border-b border-green-200 px-6 py-3">
                    <p className="text-sm text-green-800 font-medium">
                      ✓ You have approved this request on {new Date(currentUserApproval.approvedAt).toLocaleDateString()}
                    </p>
                  </div>
                )}
                {!canApproveNow && currentUserApproval?.status === 'PENDING' && (
                  <div className="bg-yellow-50 border-b border-yellow-200 px-6 py-3">
                    <p className="text-sm text-yellow-800 font-medium">
                      ⏳ Waiting for previous approvers to approve before you can take action
                    </p>
                  </div>
                )}
                <div className="p-6">
                  {/* Selection and Header */}
                  <div className="flex justify-between items-start mb-6">
                    <div className="flex items-start gap-4 flex-1">
                      {/* Selection Checkbox */}
                      {isSelectable && (
                        <div className="flex items-start pt-1">
                          <label className="flex items-center cursor-pointer hover:bg-slate-100 p-2 rounded-lg transition-colors">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleSelectNoc(noc.id)}
                              className="w-5 h-5 text-indigo-600 border-slate-300 rounded focus:ring-2 focus:ring-indigo-500"
                            />
                          </label>
                        </div>
                      )}
                      
                      {/* Header Content */}
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-slate-900 mb-2">
                          {nocTypeLabels[noc.nocType]}
                        </h3>
                        <p className="text-sm text-slate-600">
                          Request ID: {noc.id.slice(0, 8)}... • Submitted on{' '}
                          {new Date(noc.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <Link
                      to={`/noc/${noc.id}`}
                      className="text-primary hover:text-primary-dark text-sm font-medium whitespace-nowrap ml-4"
                    >
                      View Full Details →
                    </Link>
                  </div>

                  {/* Student Info */}
                  <div className="grid md:grid-cols-3 gap-4 mb-6 bg-slate-50 rounded-lg p-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        Student Name
                      </label>
                      <p className="font-medium text-slate-900">{noc.student.name}</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">USN</label>
                      <p className="font-medium text-slate-900">{noc.student.usn}</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        Department
                      </label>
                      <p className="font-medium text-slate-900">{noc.student.department}</p>
                    </div>
                  </div>

                  {/* Reason */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-slate-600 mb-2">Reason</label>
                    <p className="text-slate-900 bg-slate-50 rounded-lg p-4">
                      {noc.reason}
                    </p>
                  </div>

                  {noc.additionalDetails && (
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-slate-600 mb-2">
                        Additional Details
                      </label>
                      <p className="text-slate-900 bg-slate-50 rounded-lg p-4">
                        {noc.additionalDetails}
                      </p>
                    </div>
                  )}

                  {/* Approval Status */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-slate-600 mb-3">
                      Approval Progress (Sequential Order)
                    </label>
                    <div className="flex gap-2 flex-wrap">
                      {(() => {
                        const approvalOrder = ['ACCOUNTS_SECTION', 'LIBRARY_STAFF', 'HOD', 'EXAM_SECTION'];
                        const sortedApprovals = [...noc.approvals].sort((a, b) => 
                          approvalOrder.indexOf(a.approverRole) - approvalOrder.indexOf(b.approverRole)
                        );
                        return sortedApprovals.map((approval, index) => (
                          <span
                            key={approval.id}
                            className={`px-3 py-1 rounded-full text-sm font-medium ${
                              approval.status === 'APPROVED'
                                ? 'bg-emerald-100 text-emerald-800'
                                : approval.status === 'REJECTED'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-slate-100 text-slate-600'
                            }`}
                          >
                            {index + 1}. {approval.approverRole.replace('_', ' ')}: {approval.status}
                          </span>
                        ));
                      })()}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  {!hasApproved && (
                    <div className="flex gap-4">
                      <button
                        onClick={() => {
                          const remarks = prompt(hasRejected ? 'Update remarks (optional):' : 'Add remarks (optional):');
                          if (remarks !== null) {
                            handleApproval(noc.id, 'APPROVED', remarks);
                          }
                        }}
                        disabled={processingId === noc.id || !canApproveNow}
                        className="flex-1 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {processingId === noc.id ? (
                          <span className="flex items-center justify-center gap-2">
                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                            Processing...
                          </span>
                        ) : (
                          hasRejected ? '✓ Change to Approve' : '✓ Approve'
                        )}
                      </button>
                      {!hasRejected && (
                        <button
                          onClick={() => {
                            const remarks = prompt('Reason for rejection (required):');
                            if (remarks && remarks.trim()) {
                              handleApproval(noc.id, 'REJECTED', remarks);
                            } else if (remarks !== null) {
                              toast.error('Remarks are required for rejection');
                            }
                          }}
                          disabled={processingId === noc.id || !canApproveNow}
                          className="flex-1 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          ✗ Reject
                        </button>
                      )}
                    </div>
                  )}
                  {hasRejected && currentUserApproval.remarks && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-900">
                        <strong>Your previous rejection reason:</strong> {currentUserApproval.remarks}
                      </p>
                    </div>
                  )}
                  {hasApproved && currentUserApproval.remarks && (
                    <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-900">
                        <strong>Your approval remarks:</strong> {currentUserApproval.remarks}
                      </p>
                    </div>
                  )}
                  
                  {/* Reject button for approved NOCs (only if allowed) */}
                  {hasApproved && activeTab === 'approved' && canUserRejectApproved(noc) && (
                    <div className="mt-4">
                      <button
                        onClick={() => {
                          if (window.confirm('Are you sure you want to reject this already approved NOC? This action will revert the approval status.')) {
                            const remarks = prompt('Reason for rejection (required):');
                            if (remarks && remarks.trim()) {
                              handleApproval(noc.id, 'REJECTED', remarks);
                            } else if (remarks !== null) {
                              toast.error('Remarks are required for rejection');
                            }
                          }
                        }}
                        disabled={processingId === noc.id}
                        className="w-full py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {processingId === noc.id ? 'Processing...' : '✗ Reject This Approved NOC'}
                      </button>
                      {userRole === 'EXAM_SECTION' ? (
                        <p className="text-xs text-slate-500 mt-2 text-center">
                          EXAM_SECTION cannot reject approved NOCs
                        </p>
                      ) : (
                        <p className="text-xs text-slate-500 mt-2 text-center">
                          You can reject this NOC because no subsequent approvers have approved it yet
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
            })}
          </div>
        )}
        
        {/* Pagination Controls */}
        {allDisplayRequests.length > 0 && totalPages > 1 && (
          <div className="mt-8 bg-white rounded-lg shadow-md p-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              {/* Items per page selector */}
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-slate-700">
                  Items per page:
                </label>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              
              {/* Page info */}
              <div className="text-sm text-slate-600">
                Showing {startIndex + 1} to {Math.min(endIndex, allDisplayRequests.length)} of {allDisplayRequests.length} requests
              </div>
              
              {/* Page navigation */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="First page"
                >
                  ⏮️
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Previous page"
                >
                  ◀️
                </button>
                
                {/* Page numbers */}
                <div className="flex items-center gap-1">
                  {(() => {
                    const pages = [];
                    const maxVisible = 5;
                    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
                    
                    if (endPage - startPage < maxVisible - 1) {
                      startPage = Math.max(1, endPage - maxVisible + 1);
                    }
                    
                    if (startPage > 1) {
                      pages.push(
                        <span key="start-ellipsis" className="px-2 text-slate-500">...</span>
                      );
                    }
                    
                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => setCurrentPage(i)}
                          className={`px-3 py-2 rounded-lg transition-colors ${
                            currentPage === i
                              ? 'bg-indigo-600 text-white font-semibold'
                              : 'border border-slate-300 hover:bg-slate-50'
                          }`}
                        >
                          {i}
                        </button>
                      );
                    }
                    
                    if (endPage < totalPages) {
                      pages.push(
                        <span key="end-ellipsis" className="px-2 text-slate-500">...</span>
                      );
                    }
                    
                    return pages;
                  })()}
                </div>
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Next page"
                >
                  ▶️
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Last page"
                >
                  ⏭️
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Approvals;
